import { Controller } from '@hotwired/stimulus';

// Drafts live at module level: a Turbo visit replaces the DOM, and this
// controller with it, but not the JavaScript context. The re-rendered input
// only knows the value the server was sent; what was typed meanwhile, and the
// caret, would be lost with the old element.
const drafts = new Map();

export default class AutoSubmitController extends Controller {
  static values = { delay: { type: Number, default: 0 } };

  connect() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.#form?.addEventListener('turbo:submit-start', () => this.#markSent(), { signal });
    this.#form?.addEventListener('turbo:submit-end', (event) => {
      this.submitted = Boolean(event.detail.success);
    }, { signal });

    this.#restoreDraft();
  }

  submit() {
    clearTimeout(this.timeout);
    this.#rememberDraft();

    if (this.delayValue === 0) {
      this.#submitForm();
      return;
    }

    this.timeout = setTimeout(() => this.#submitForm(), this.delayValue);
  }

  disconnect() {
    clearTimeout(this.timeout);
    this.abortController.abort();
    if (!this.#carries()) return;

    if (this.submitted) {
      this.#handOverDraft();
    } else {
      drafts.delete(this.element.id);
    }
  }

  get #form() {
    return this.element.closest('form');
  }

  #submitForm() {
    this.#form?.requestSubmit();
  }

  // Selects and number inputs either lack setSelectionRange or throw on it.
  #carries() {
    return Boolean(this.element.id) && this.element.type === 'text';
  }

  #rememberDraft() {
    if (!this.#carries()) return;

    drafts.set(this.element.id, {
      ...drafts.get(this.element.id),
      value: this.element.value,
      caret: this.element.selectionStart,
    });
  }

  // Stamped on turbo:submit-start rather than in #submitForm so that an Enter
  // key, which submits without going through here, counts as sent too.
  #markSent() {
    clearTimeout(this.timeout);
    if (!this.#carries()) return;

    this.#rememberDraft();
    drafts.get(this.element.id).sent = this.element.value;
  }

  // The answering page consumes the draft in its own connect(); when that page
  // has no such input (redirect, cancelled render) the draft must not wait for
  // a later, unrelated visit.
  #handOverDraft() {
    const draft = drafts.get(this.element.id);
    if (!draft) return;

    draft.handedOver = true;
    setTimeout(() => {
      if (drafts.get(this.element.id)?.handedOver) drafts.delete(this.element.id);
    }, 0);
  }

  #restoreDraft() {
    if (!this.#carries() || !drafts.has(this.element.id)) return;

    const draft = drafts.get(this.element.id);
    drafts.delete(this.element.id);

    const unsent = draft.value !== draft.sent;
    if (unsent) this.element.value = draft.value;

    this.element.focus();
    this.element.setSelectionRange(draft.caret, draft.caret);

    if (unsent) this.#submitWhenLoaded();
  }

  // Submitting inside connect() would cancel the visit still rendering this
  // page (Navigator#stop), and with it turbo:load.
  #submitWhenLoaded() {
    document.addEventListener('turbo:load', () => this.#submitForm(), {
      once: true,
      signal: this.abortController.signal,
    });
  }
}
